import { useState } from "react";
import { toast } from "sonner";
import Nav from "@/components/sections/Nav";
import Pricing from "@/components/sections/Pricing";
import Footer from "@/components/sections/Footer";
import { FadeIn } from "@/components/FadeIn";
import { createCheckout } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";

/**
 * Persona-Landingpage-Vorlage.
 * Alle SEO-relevanten Inhalte kommen als Props herein.
 */
export default function PersonaPage({
  slug,           // z. B. "trauerrede-mann"
  metaTitle,
  metaDescription,
  eyebrow,        // z. B. "Trauerrede · Vater · Bruder · Sohn"
  headline,       // JSX oder String
  intro,          // längere warme Einleitung
  heroImage,      // Unsplash-URL
  heroImageAlt,
  questionsTitle = "Was wir Sie fragen",
  questions,      // Array of {q, why}
  exampleTitle = "Ein Beispiel — so klingt ein Entwurf",
  exampleQuote,   // String (Beispieltext)
  faq,            // Array of {q, a} – wird auch als JSON-LD ausgegeben
  relatedTitle = "Verwandte Anlässe",
  related,        // Array of {label, to}
}) {
  const canonical = `https://oratiorede.de/${slug}`;
  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    canonical,
    ogImage: "https://oratiorede.de/og-image.jpg",
  });

  const [loading, setLoading] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState(null);

  const scrollToPricing = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCheckout = async (packageId = "oratio_draft") => {
    setLoading(true);
    setLoadingPackage(packageId);
    try {
      const data = await createCheckout(window.location.origin, packageId);
      window.location.href = data.url;
    } catch (e) {
      console.error(e);
      toast.error("Zahlung konnte nicht gestartet werden. Bitte später erneut versuchen.");
      setLoading(false);
      setLoadingPackage(null);
    }
  };

  const faqLd = faq && faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(({ q, a }) => ({
      "@type": "Question",
      "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a },
    })),
  } : null;

  return (
    <div className="bg-cream min-h-screen">
      <Nav onCta={scrollToPricing} />

      {/* Hero */}
      <section className="relative pt-40 pb-16 md:pt-56 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={heroImage}
            alt={heroImageAlt}
            className="w-full h-full object-cover opacity-[0.5]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-cream/10 via-cream/30 to-cream" />
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="label-eyebrow mb-8" data-testid={`persona-eyebrow-${slug}`}>{eyebrow}</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1
              data-testid={`persona-h1-${slug}`}
              className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.1] tracking-tight text-charcoal max-w-4xl"
            >
              {headline}
            </h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-10 text-lg md:text-xl leading-relaxed text-ink max-w-2xl font-serif">
              {intro}
            </p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <button onClick={scrollToPricing} data-testid={`persona-cta-${slug}`} className="btn-primary">
                Entwurf erstellen
              </button>
              <a href="/" className="btn-secondary">Zur Startseite</a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Questions */}
      {questions && questions.length > 0 && (
        <section className="py-20 md:py-28 border-t border-umber/10">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <FadeIn>
              <p className="label-eyebrow mb-6">Fragebogen</p>
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal leading-tight max-w-3xl">
                {questionsTitle}
              </h2>
              <p className="mt-6 font-serif text-lg text-ink max-w-2xl">
                Acht behutsame Fragen, aus denen wir Ihren Entwurf verfassen. Kein Fragebogen wie in einer Behörde — eher wie ein leises Gespräch.
              </p>
            </FadeIn>
            <div className="mt-14 grid md:grid-cols-2 gap-8 md:gap-10">
              {questions.map((item, i) => (
                <FadeIn key={i} delay={0.05 * i}>
                  <div className="flex gap-6">
                    <span className="font-serif text-4xl text-umber/60 leading-none pt-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-serif text-xl text-charcoal leading-snug">{item.q}</h3>
                      <p className="mt-2 font-serif text-base text-ink leading-relaxed">{item.why}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Example Quote */}
      {exampleQuote && (
        <section className="py-24 md:py-32 bg-paper/40 border-t border-umber/10">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <FadeIn>
              <p className="label-eyebrow mb-6 text-center">Beispiel</p>
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal text-center leading-tight">
                {exampleTitle}
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <blockquote className="mt-14 font-serif text-xl md:text-2xl text-charcoal leading-[1.7] italic border-l-2 border-umber/40 pl-8">
                {exampleQuote}
              </blockquote>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-8 font-sans text-sm text-ink/70 text-center italic">
                Ein anonymisiertes Beispiel — Ihr Entwurf klingt persönlicher, weil er aus Ihren Antworten entsteht.
              </p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq && faq.length > 0 && (
        <section className="py-24 md:py-32 border-t border-umber/10">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <FadeIn>
              <p className="label-eyebrow mb-6">Häufige Fragen</p>
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal leading-tight">
                Was Angehörige uns oft fragen
              </h2>
            </FadeIn>
            <div className="mt-14 space-y-10">
              {faq.map(({ q, a }, i) => (
                <FadeIn key={i} delay={0.04 * i}>
                  <div className="border-b border-umber/10 pb-8 last:border-0">
                    <h3 className="font-serif text-xl text-charcoal leading-snug">{q}</h3>
                    <p className="mt-3 font-serif text-base text-ink leading-relaxed">{a}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing */}
      <Pricing onCta={handleCheckout} loading={loading} loadingPackage={loadingPackage} />

      {/* Related personas */}
      {related && related.length > 0 && (
        <section className="pb-20 border-t border-umber/10 pt-16">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <p className="label-eyebrow mb-6 text-center">{relatedTitle}</p>
            <div className="flex flex-wrap justify-center gap-3">
              {related.map(({ label, to }) => (
                <a
                  key={to}
                  href={to}
                  className="chip"
                  data-testid={`persona-related-${to.replace("/", "")}`}
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      {faqLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
        />
      )}
    </div>
  );
}
